import React, { useContext, useState, useRef } from 'react';
import { UserContext } from '../contexts/UserContext';
import ProfileCard from './ProfileCard';
import { QrCode, Download, Share2, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';

const UserPassport = ({ onClose }) => {
  const { user } = useContext(UserContext);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const passportRef = useRef(null);

  // Generate unique user ID for QR code - points to mobile Powerpass
  const generateUserQRUrl = () => {
    if (!user?.id) return 'https://ventro.energy';
    return `${window.location.origin}/powerpass/${user.id}`;
  };

  // Get the URL that QR code should point to
  const powerpassUrl = generateUserQRUrl();
  console.log('QR Code URL:', powerpassUrl, 'User ID:', user?.id);

  // Get user skills/qualifications
  const getUserSkills = () => {
    const skills = [];

    if (user?.qualifications && user.qualifications.length > 0) {
      // Take up to 6 most recent qualifications
      const recentQualifications = user.qualifications
        .slice(0, 6)
        .map(q => q.qualification_type_name || q.name);
      skills.push(...recentQualifications);
    }

    // Add some default skills if none available
    if (skills.length === 0) {
      skills.push('Platform Member', 'Certified Professional');
    }

    return skills;
  };

  // Download passport as image - Simple QR code
  const handleDownloadPassport = async () => {
    try {
      // Create a simple canvas with QR code
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size - square format for better social sharing
      canvas.width = 800;
      canvas.height = 800;

      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add subtle border
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);

      // Draw title at top
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 42px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Powerpass', canvas.width / 2, 90);

      ctx.font = '20px Inter, sans-serif';
      ctx.fillStyle = '#6b7280';
      ctx.fillText('Digital Identity Card', canvas.width / 2, 125);

      // Get QR code element and draw it
      const qrElement = passportRef.current?.querySelector('.pc-qr-code svg');
      if (qrElement) {
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(qrElement);
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        const qrImage = new Image();
        qrImage.src = url;

        await new Promise((resolve) => {
          qrImage.onload = resolve;
        });

        // Draw QR code centered - larger
        const qrSize = 400;
        const qrX = (canvas.width - qrSize) / 2;
        const qrY = 180;

        // Add white background for QR code
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);

        // Add border around QR code
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 2;
        ctx.strokeRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);

        ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

        URL.revokeObjectURL(url);
      }

      // Draw user info at bottom
      ctx.fillStyle = '#000000';
      const userName = `${user?.firstName} ${user?.lastName}`;

      // Dynamically adjust font size for long names
      let fontSize = 40;
      ctx.font = `bold ${fontSize}px Inter, sans-serif`;
      let textWidth = ctx.measureText(userName).width;
      const maxWidth = canvas.width * 0.85; // 85% of canvas width

      while (textWidth > maxWidth && fontSize > 20) {
        fontSize -= 2;
        ctx.font = `bold ${fontSize}px Inter, sans-serif`;
        textWidth = ctx.measureText(userName).width;
      }

      ctx.fillText(userName, canvas.width / 2, 640);

      ctx.font = '18px Inter, sans-serif';
      ctx.fillStyle = '#6b7280';
      ctx.fillText(`WINDA ID: ${user?.winda_id || 'N/A'}`, canvas.width / 2, 675);

      // Add position/company if available
      const position = user?.position || user?.company_name;
      if (position) {
        ctx.font = '16px Inter, sans-serif';
        ctx.fillStyle = '#9ca3af';
        ctx.fillText(position, canvas.width / 2, 705);
      }

      // Footer text
      ctx.font = '14px Inter, sans-serif';
      ctx.fillStyle = '#9ca3af';
      ctx.fillText('Scan to view full profile', canvas.width / 2, 750);

      // Download
      const link = document.createElement('a');
      link.download = `${user?.firstName}_${user?.lastName}_powerpass.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
    } catch (error) {
      console.error('Error downloading passport:', error);
      alert('Failed to download passport. Please try again.');
    }
  };

  // Share passport
  const handleShare = async () => {
    const shareUrl = generateUserQRUrl();
    const shareText = `Check out my Ventro profile: ${user?.firstName} ${user?.lastName}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Ventro Profile',
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Profile link copied to clipboard!');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4 overflow-y-auto">
      <div className="relative w-full max-w-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-50 w-10 h-10 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center text-black border-2 border-gray-200 transition-all shadow-xl"
          aria-label="Close passport"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Action Buttons */}
        <div className="absolute -top-3 -left-3 z-50 flex gap-2">
          <button
            onClick={handleDownloadPassport}
            className="w-10 h-10 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center text-black border-2 border-gray-200 transition-all shadow-xl"
            aria-label="Download passport"
            title="Download Passport"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={handleShare}
            className="w-10 h-10 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center text-black border-2 border-gray-200 transition-all shadow-xl"
            aria-label="Share passport"
            title="Share Profile"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>

        {/* Passport Card */}
        <div ref={passportRef} className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl overflow-hidden">
          <div className="flex flex-col items-center">
            {/* Header - White Banner */}
            <div className="w-full bg-gradient-to-r from-white via-gray-50 to-white shadow-lg py-4 px-6 border-b-2 border-gray-200">
              <div className="flex items-center justify-center gap-3 mb-2">
                <img
                  src="https://ventro.energy/ventro.png"
                  alt="Ventro Logo"
                  className="h-10"
                />
              </div>
              <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-1 tracking-tight" style={{ fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                  Powerpass
                </h1>
                <p className="text-sm font-medium text-gray-600 tracking-wide" style={{ fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                  Digital Identity Card
                </p>
              </div>

              {/* Contact Info */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-2">
                  <QrCode className="w-3.5 h-3.5" />
                  <span className="font-medium">Scan QR code to view full profile</span>
                </div>
                <div className="flex items-center justify-center gap-3 text-sm font-medium text-gray-700">
                  <span>{user?.email || 'No email provided'}</span>
                  {user?.phone_number && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span>{user.phone_number}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Card */}
            <div className="transform scale-90 hover:scale-95 transition-transform duration-300 py-4">
              <ProfileCard
                name={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
                title={user?.position || user?.company_name || 'Platform Member'}
                handle={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'user'}
                status={`WINDA ID: ${user?.winda_id || user?.windaId || 'N/A'}`}
                contactText=""
                avatarUrl={user?.profile_picture || 'https://via.placeholder.com/400'}
                showUserInfo={true}
                enableTilt={true}
                enableMobileTilt={true}
                qrCodeUrl={powerpassUrl}
                iconUrl=""
                grainUrl=""
                showBehindGradient={true}
                skills={getUserSkills()}
                showVerifiedBadge={true}
              />
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-3 text-center">
          <p className="text-xs text-white/70">
            Share your digital passport with others or download it for offline use
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserPassport;
